import React, { RefObject, useState } from 'react'
import { Box, Button, Flex, Label, Select, Text, toast } from '@workwhile/ui'
import {
  PaymentProvider,
  PaymentProvider1099,
  UserOutstandingBalance,
} from 'api/payments'
import { User } from 'api/typings'
import { useUpdatePaymentProvider1099 } from 'queries/payments/useUpdatePaymentProvider1099'
import {
  useUserExternalBankAccounts,
  useUserPaymentMethods,
} from 'queries/payments/useUserPaymentMethods'
import { AmountEntry } from './AmountEntry'
import { BankAccountBox } from './BankAccountBox'

interface HighnoteBoxProps {
  user: User
  current1099PaymentProvider: PaymentProvider
  outstandingBalance: UserOutstandingBalance
  highnoteBoxRef: RefObject<HTMLDivElement | null>
}

const PROVIDER_1099_OPTIONS: { label: string; value: PaymentProvider1099 }[] = [
  { label: 'Stripe', value: 'stripe' },
  { label: 'Highnote', value: 'highnote' },
]

const isSupportedProvider1099 = (
  provider: PaymentProvider
): provider is PaymentProvider1099 =>
  PROVIDER_1099_OPTIONS.some((option) => option.value === provider)

export const HighnoteBox = ({
  user,
  current1099PaymentProvider,
  outstandingBalance,
  highnoteBoxRef,
}: HighnoteBoxProps) => {
  const isHighnote = current1099PaymentProvider === 'highnote'
  const [provider, setProvider] = useState<PaymentProvider1099 | null>(
    isSupportedProvider1099(current1099PaymentProvider)
      ? current1099PaymentProvider
      : null
  )
  const updateProviderMutation = useUpdatePaymentProvider1099()
  const userId = user.id ? Number(user.id) : 0

  const { data: paymentMethodsData } = useUserPaymentMethods(
    { userId },
    { enabled: isHighnote && !!user.id }
  )
  const { data: externalBankAccountsData } = useUserExternalBankAccounts(
    { userId },
    { enabled: isHighnote && !!user.id }
  )

  // Only display active methods/accounts for now
  const paymentMethod =
    paymentMethodsData?.paymentMethods.find((pm) => !pm.archivedAt) ?? null
  const externalBankAccount =
    externalBankAccountsData?.externalBankAccounts.find(
      (eba) => !eba.archivedAt
    ) ?? null

  const earningsWithHighnote = outstandingBalance.earningsWithHighnote ?? 0
  const completedHighnotePayouts =
    outstandingBalance.completedHighnotePayouts ?? 0
  const pendingHighnotePayouts = outstandingBalance.pendingHighnotePayouts ?? 0

  const submitProviderUpdate = () => {
    if (!user.id) return
    if (!provider || provider === current1099PaymentProvider) return
    updateProviderMutation.mutate(
      { userId: Number(user.id), paymentProvider1099: provider },
      {
        onSuccess: () => toast.success(`1099 provider set to ${provider}`),
        onError: () => toast.error('Failed to update 1099 provider'),
      }
    )
  }

  return (
    <Box
      ref={highnoteBoxRef}
      position="relative"
      bg="white"
      border="1px solid"
      borderColor="border"
      borderRadius="medium"
      p={3}
      minWidth="250px"
      maxWidth="100%"
      width="100%"
      boxShadow="small"
      zIndex={2}
    >
      <Flex alignItems="center" gap={2} mb={3}>
        <Text fontWeight="bold" color="primary" fontSize={3}>
          Highnote
        </Text>
        <Label variant="primary">1099</Label>
        {isHighnote ? (
          <Label variant="warning">Current 1099 Provider</Label>
        ) : null}
      </Flex>

      <Flex alignItems="center" gap={2} mb={3} flexWrap="wrap">
        <Text>Change 1099 provider</Text>
        <Box
          minWidth="180px"
          flex="1"
          style={{ position: 'relative', zIndex: 10 }}
        >
          <Select
            name="newProvider1099"
            placeholder="Select a provider"
            value={
              PROVIDER_1099_OPTIONS.find(
                (option) => option.value === provider
              ) ?? null
            }
            onChange={(option) => {
              const { value } = option as { value: PaymentProvider1099 }
              setProvider(value)
            }}
            options={PROVIDER_1099_OPTIONS}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </Box>
        <Button
          type="confirm"
          disabled={
            !user.id || !provider || provider === current1099PaymentProvider
          }
          loading={updateProviderMutation.isPending}
          onClick={submitProviderUpdate}
        >
          Set Provider
        </Button>
      </Flex>

      {isHighnote ? (
        <Flex flexDirection="row" gap={3}>
          <Box
            bg="background"
            border="1px solid"
            borderColor="border"
            borderRadius="small"
            p={3}
            flex={1}
          >
            <Text fontWeight="bold" fontSize={2} mb={2}>
              Highnote Ledger
            </Text>
            <Text mb={3} fontSize={2} color="lightText">
              Earnings held until cashout or paycheck
            </Text>
            <AmountEntry
              label="Live balance"
              tooltip="Earnings - Payouts (includes pending earnings available to cash out)"
              labelProps={{ fontWeight: 'bold' }}
              value={earningsWithHighnote + completedHighnotePayouts}
            />
            <AmountEntry
              ml={2}
              labelProps={{ color: 'lightText' }}
              label="Earnings"
              value={earningsWithHighnote}
            />
            <AmountEntry
              ml={2}
              labelProps={{ color: 'lightText' }}
              label="Payouts"
              value={completedHighnotePayouts}
            />
            <AmountEntry
              ml={2}
              labelProps={{ color: 'lightText', fontStyle: 'italic' }}
              label="(Pending payouts)"
              value={pendingHighnotePayouts}
            />
          </Box>
          <Box
            bg="background"
            border="1px solid"
            borderColor="border"
            borderRadius="small"
            p={3}
            minWidth="200px"
            flex={1}
          >
            <Text fontWeight="bold" fontSize={2} mb={2}>
              Payout Options
            </Text>
            <Text color="lightText" mb={2}>
              <Text fontWeight="bold" inline>
                Instant Payout:
              </Text>{' '}
              User can request immediate payout to a debit card
            </Text>
            {paymentMethod ? (
              <BankAccountBox
                account={{
                  accountType: 'card',
                  displayName: paymentMethod.brand,
                  last4: paymentMethod.last4,
                  status: null,
                  stripeIsDefault: null,
                  stripeCanInstantPay: null,
                }}
                id={paymentMethod.id}
                name={paymentMethod.cardHolderName}
                expiry={`${String(paymentMethod.expiryMonth).padStart(2, '0')}/${paymentMethod.expiryYear}`}
                badges={['Instant']}
              />
            ) : (
              <Text fontWeight="bold" color="error" mb={2}>
                No debit card for instant payout
              </Text>
            )}
            <Text color="lightText" mb={2} mt={2}>
              <Text fontWeight="bold" inline>
                Paycheck:
              </Text>{' '}
              Run weekly on Monday to an external bank account
            </Text>
            {externalBankAccount ? (
              <BankAccountBox
                account={{
                  accountType: externalBankAccount.bankAccountType,
                  displayName: 'Bank account',
                  last4: externalBankAccount.last4,
                  status: null,
                  stripeIsDefault: null,
                  stripeCanInstantPay: null,
                }}
                id={externalBankAccount.id}
                name={externalBankAccount.name}
                routingNumber={externalBankAccount.routingNumber}
                badges={['Default paycheck']}
              />
            ) : (
              <Text fontWeight="bold" color="error">
                No bank account for paycheck
              </Text>
            )}
          </Box>
        </Flex>
      ) : null}
    </Box>
  )
}
